import { useState, useEffect } from "react";
import { useTheme } from "../../context/ThemeContext";
const cards = [
  {
    id: "stake",
    label: "My Stake",
    value: "$0.00",
    delta: "No active stake",
    deltaType: "neutral",
    icon: "🔒",
    accent: ["#7C3AED", "#5B21B6"],
    iconBg: ["#EDE9FE", "#2D1B69"],
    group: "core",
  },
  {
    id: "income",
    label: "Total Income",
    value: "$0.00",
    delta: "Awaiting rewards",
    deltaType: "neutral",
    icon: "📈",
    accent: ["#059669", "#047857"],
    iconBg: ["#D1FAE5", "#064E3B"],
    group: "core",
  },
  {
    id: "withdraw",
    label: "Total Withdraw",
    value: "$0.00",
    delta: "No withdrawals yet",
    deltaType: "neutral",
    icon: "💸",
    accent: ["#DC2626", "#B91C1C"],
    iconBg: ["#FEE2E2", "#7F1D1D"],
    group: "core",
  },
  {
    id: "available",
    label: "Available Amount",
    value: "$0.00",
    delta: "Ready to use",
    deltaType: "positive",
    icon: "💰",
    accent: ["#0284C7", "#0369A1"],
    iconBg: ["#E0F2FE", "#0C4A6E"],
    group: "core",
  },
  {
    id: "direct",
    label: "Direct Business",
    value: "$0.00",
    delta: "0 referrals",
    deltaType: "neutral",
    icon: "🤝",
    accent: ["#DB2777", "#BE185D"],
    iconBg: ["#FCE7F3", "#831843"],
    group: "business",
  },
  {
    id: "team",
    label: "Team Business",
    value: "$0.00",
    delta: "Team inactive",
    deltaType: "neutral",
    icon: "👥",
    accent: ["#16A34A", "#15803D"],
    iconBg: ["#DCFCE7", "#14532D"],
    group: "business",
  },
  {
    id: "staking",
    label: "Staking Reward",
    value: "$0.00",
    delta: "Starts on stake",
    deltaType: "neutral",
    icon: "🪙",
    accent: ["#D97706", "#B45309"],
    iconBg: ["#FEF3C7", "#78350F"],
    group: "rewards",
  },
  {
    id: "rank",
    label: "Rank Reward",
    value: "$0.00",
    delta: "No rank yet",
    deltaType: "neutral",
    icon: "🏆",
    accent: ["#7C3AED", "#5B21B6"],
    iconBg: ["#EDE9FE", "#2D1B69"],
    group: "rewards",
  },
  {
    id: "level",
    label: "Level Income",
    value: "$0.00",
    delta: "Unlock via team",
    deltaType: "neutral",
    icon: "📊",
    accent: ["#0891B2", "#0E7490"],
    iconBg: ["#CFFAFE", "#164E63"],
    group: "rewards",
  },
];

const groups = [
  { key: "core", label: "Core Balance" },
  { key: "business", label: "Business" },
  { key: "rewards", label: "Rewards" },
];

function DashboardCard({ card, isDarkMode, index }) {
  const [hovered, setHovered] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), index * 80);
    return () => clearTimeout(t);
  }, [index]);

  const accentColor = card.accent[0];
  const iconBg = isDarkMode ? card.iconBg[1] : card.iconBg[0];

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: "relative",
        borderRadius: 12,
        padding: "10px 14px",
        display: "flex",
        alignItems: "center",
        gap: 12,
        background: isDarkMode
          ? hovered ? "rgba(255,255,255,0.07)" : "rgba(255,255,255,0.04)"
          : hovered ? "#ffffff" : "#f9f9fb",
        border: `1px solid ${
          hovered
            ? accentColor + "55"
            : isDarkMode
            ? "rgba(255,255,255,0.09)"
            : "rgba(0,0,0,0.07)"
        }`,
        boxShadow: hovered
          ? `0 6px 20px ${accentColor}20`
          : isDarkMode
          ? "0 1px 6px rgba(0,0,0,0.25)"
          : "0 1px 4px rgba(0,0,0,0.04)",
        cursor: "pointer",
        transition: "all 0.25s cubic-bezier(0.4,0,0.2,1)",
        transform: visible
          ? hovered ? "translateY(-2px) scale(1.01)" : "translateY(0) scale(1)"
          : "translateY(10px) scale(0.97)",
        opacity: visible ? 1 : 0,
        overflow: "hidden",
      }}
    >
      {/* Left accent bar */}
      <div
        style={{
          position: "absolute",
          top: 0, left: 0, bottom: 0,
          width: 3,
          background: `linear-gradient(180deg, ${card.accent[0]}, ${card.accent[1]})`,
          borderRadius: "12px 0 0 12px",
          opacity: hovered ? 1 : 0.6,
          transition: "opacity 0.25s",
        }}
      />

      {/* Glow blob */}
      <div
        style={{
          position: "absolute",
          top: -20, right: -10,
          width: 70, height: 70,
          borderRadius: "50%",
          background: accentColor,
          opacity: hovered ? 0.06 : 0.02,
          filter: "blur(16px)",
          transition: "opacity 0.3s",
          pointerEvents: "none",
        }}
      />

      {/* Icon */}
      <div
        style={{
          width: 54, height: 55,
          flexShrink: 0,
          borderRadius: 9,
          background: iconBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 38,
          border: `1px solid ${accentColor}33`,
          transition: "transform 0.2s",
          transform: hovered ? "scale(1.1) rotate(-5deg)" : "scale(1) rotate(0deg)",
          marginLeft: 6,
        }}
      >
        {card.icon}
      </div>

      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: isDarkMode ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.38)",
            marginBottom: 2,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {card.label}
        </div>
        <div
          style={{
            fontSize: 18,
            fontWeight: 700,
            color: isDarkMode ? "#ffffff" : "#0f0f0f",
            fontFamily: "'DM Mono', 'Fira Mono', monospace",
            letterSpacing: "-0.02em",
            lineHeight: 1.2,
          }}
        >
          {card.value}
        </div>
      </div>
    </div>
  );
}

export default function DashboardCards() {
  const { isDarkMode } = useTheme(); // sirf isDarkMode — no toggle button, no dark mode state

  return (
    <div style={{ fontFamily: "'DM Sans', 'Nunito', sans-serif" }}>
      {groups.map((group) => {
        const groupCards = cards.filter((c) => c.group === group.key);
        return (
          <div key={group.key} style={{ marginBottom: 28 }}>

            {/* Section label */}
            <div
              style={{
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: isDarkMode ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.3)",
                marginBottom: 12,
                paddingLeft: 2,
              }}
            >
              {group.label}
            </div>

            {/* Cards grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: 10,
              }}
            >
              {groupCards.map((card, i) => (
                <DashboardCard
                  key={card.id}
                  card={card}
                  isDarkMode={isDarkMode}
                  index={i + groups.findIndex((g) => g.key === group.key) * 3}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}